// SchemePay Reconciliation Dashboard - AI Insights & Audit Engine
import { ReconciledItem, ReconciliationSummary } from '../types/reconciliation';

export interface AiMismatchAnalysis {
  paymentId: string;
  category: 'Split Bill Discrepancy' | 'Timing Delay' | 'Duplicate Bill Risk' | 'Payment Gateway Orphan' | 'Refund Reversal Pending' | 'Exact Match Verified';
  confidenceScore: number; // 0 - 100
  summary: string;
  technicalDetails: string[];
  remediationSteps: string[];
}

/**
 * Generates an AI-driven root-cause diagnostic for any reconciliation item
 */
export function generateAiMismatchAnalysis(item: ReconciledItem): AiMismatchAnalysis {
  switch (item.status) {
    case 'Exact Match':
      return {
        paymentId: item.paymentId,
        category: 'Exact Match Verified',
        confidenceScore: 100,
        summary: `Payment ID ${item.paymentId} has been perfectly balanced across ${item.billCount} ERP bill(s).`,
        technicalDetails: [
          `Total Razorpay captured: ₹${item.razorpayAmount.toLocaleString('en-IN')}`,
          `ERP aggregation total: ₹${item.erpTotalAmount.toLocaleString('en-IN')}`,
          `Net variance: ₹0.00`,
          `ERP Bill References: ${item.billNumbers.join(', ')}`,
        ],
        remediationSteps: [
          'Automatic reconciliation approved.',
          'Post clearance marker in General Ledger clearing account.',
        ],
      };

    case 'Partial Match':
      return {
        paymentId: item.paymentId,
        category: 'Split Bill Discrepancy',
        confidenceScore: 92,
        summary: `Under-billed in ERP: Customer deposited ₹${item.razorpayAmount.toLocaleString('en-IN')}, but ERP invoices only total ₹${item.erpTotalAmount.toLocaleString('en-IN')}. Outstanding balance of ₹${item.differenceAmount.toLocaleString('en-IN')}.`,
        technicalDetails: [
          `Variance detected: +₹${item.differenceAmount.toLocaleString('en-IN')} (Razorpay > ERP)`,
          `Number of existing bills linked: ${item.billCount} (${item.billNumbers.join(', ')})`,
          `Probable cause: A secondary installment bill or GST invoice was generated under a different or blank payment reference.`,
        ],
        remediationSteps: [
          `Review ERP unposted billing staging table for pending vouchers matching customer "${item.customerName}".`,
          `Search for invoice drafts dated around ${item.paymentDate} for amount ₹${item.differenceAmount.toLocaleString('en-IN')}.`,
          `Link the missing bill to Payment ID "${item.paymentId}" to reach exact match.`,
        ],
      };

    case 'Over Billed':
      const absDiff = Math.abs(item.differenceAmount);
      return {
        paymentId: item.paymentId,
        category: 'Duplicate Bill Risk',
        confidenceScore: 88,
        summary: `Over-billed in ERP: ERP bills total ₹${item.erpTotalAmount.toLocaleString('en-IN')}, which exceeds payment of ₹${item.razorpayAmount.toLocaleString('en-IN')} by ₹${absDiff.toLocaleString('en-IN')}.`,
        technicalDetails: [
          `Variance detected: -₹${absDiff.toLocaleString('en-IN')} (ERP > Razorpay)`,
          `Total bills found: ${item.billCount} (${item.billNumbers.join(', ')})`,
          `Probable cause: Duplicate bill entry, revised invoice not cancelled, or customer split the payment across two separate payment gateways.`,
        ],
        remediationSteps: [
          `Check ERP bills for duplicate item descriptions or identical bill dates.`,
          `Confirm whether one of the bills (${item.billNumbers.join(', ')}) should be marked as void or credited.`,
          `Verify if customer paid remaining ₹${absDiff.toLocaleString('en-IN')} via direct bank transfer.`,
        ],
      };

    case 'No Bill Reference':
      return {
        paymentId: item.paymentId,
        category: 'Timing Delay',
        confidenceScore: 95,
        summary: `Payment Gateway Orphan: Razorpay successfully received ₹${item.razorpayAmount.toLocaleString('en-IN')} on ${item.paymentDate}, but zero bills exist in ERP for Payment ID ${item.paymentId}.`,
        technicalDetails: [
          `No matching records in ERP database for key "${item.paymentId}".`,
          `Customer Name on Razorpay: ${item.customerName}.`,
          `Probable cause: ERP integration webhook dropped or scheme subscription bill generation background worker failed.`,
        ],
        remediationSteps: [
          `Verify Scheme App backend message queue for webhook delivery failures.`,
          `Trigger manual ERP bill generation for customer "${item.customerName}" with Payment ID ${item.paymentId}.`,
          `Confirm receipt in Razorpay Settlement account before releasing physical scheme goods.`,
        ],
      };

    case 'ERP Only':
      return {
        paymentId: item.paymentId,
        category: 'Payment Gateway Orphan',
        confidenceScore: 90,
        summary: `Uncollected ERP Bills: ${item.billCount} bill(s) totaling ₹${item.erpTotalAmount.toLocaleString('en-IN')} exist in ERP with Payment ID ${item.paymentId}, but no Razorpay payment transaction exists.`,
        technicalDetails: [
          `ERP bill total: ₹${item.erpTotalAmount.toLocaleString('en-IN')}`,
          `Bills: ${item.billNumbers.join(', ')}`,
          `Customer: ${item.customerName}`,
          `Probable cause: Payment made via RTGS/NEFT/Cash at store counter or Razorpay checkout was abandoned after ERP initiated order.`,
        ],
        remediationSteps: [
          `Query offline bank statements or counter cash collection journals for ₹${item.erpTotalAmount.toLocaleString('en-IN')}.`,
          `If order was abandoned, cancel ERP bill reservation to prevent stock lockup.`,
        ],
      };

    case 'Refunded':
      return {
        paymentId: item.paymentId,
        category: 'Refund Reversal Pending',
        confidenceScore: 96,
        summary: `Transaction Refunded: Razorpay payment of ₹${item.razorpayAmount.toLocaleString('en-IN')} was refunded (Refund Amount: ₹${item.refundAmount.toLocaleString('en-IN')}) on ${item.refundDate}.`,
        technicalDetails: [
          `Razorpay payment status: Refunded`,
          `Refund Date: ${item.refundDate}`,
          `ERP Bill Count: ${item.billCount}`,
        ],
        remediationSteps: [
          `Post a Credit Note in ERP against original bill(s) ${item.billNumbers.join(', ')}.`,
          `Ensure customer account ledger reflects refund voucher reversal.`,
        ],
      };
  }
}

/**
 * Generates an executive daily reconciliation briefing
 */
export function generateDailyExecutiveSummary(
  summary: ReconciliationSummary,
  items: ReconciledItem[]
): {
  headline: string;
  healthScore: 'Optimal' | 'Attention Required' | 'Critical Review';
  bulletPoints: string[];
  riskAnalysis: string;
} {
  const matchRate = summary.matchPercentage;
  const exceptionCount =
    summary.partialMatches + summary.overBilled + summary.noBillReference + summary.erpOnly + summary.refundedCount;

  let healthScore: 'Optimal' | 'Attention Required' | 'Critical Review' = 'Optimal';
  if (matchRate < 60 || summary.totalExceptionAmount > 50000) {
    healthScore = 'Critical Review';
  } else if (matchRate < 85 || summary.noBillReference > 2) {
    healthScore = 'Attention Required';
  }

  const bulletPoints = [
    `Reconciliation engine matched ${summary.exactMatches} payment groups (${matchRate}% match rate) with zero variance.`,
    `Total Razorpay collections processed: ₹${summary.totalCollectionAmount.toLocaleString('en-IN')} across ${summary.totalRazorpayRecords} transactions.`,
    `Total ERP bills consolidated: ${summary.totalErpRecords} bills grouped into ${summary.totalGroups} unique payment IDs.`,
    summary.noBillReference > 0
      ? `Identified ${summary.noBillReference} unlinked payments worth ₹${items
          .filter((i) => i.status === 'No Bill Reference')
          .reduce((acc, i) => acc + i.razorpayAmount, 0)
          .toLocaleString('en-IN')} requiring ERP voucher generation.`
      : 'Zero missing ERP vouchers detected.',
    summary.partialMatches > 0
      ? `Found ${summary.partialMatches} under-billed records with ₹${items
          .filter((i) => i.status === 'Partial Match')
          .reduce((acc, i) => acc + i.differenceAmount, 0)
          .toLocaleString('en-IN')} pending split bill posting.`
      : 'No split bill shortages identified.',
    summary.refundedCount > 0
      ? `${summary.refundedCount} refunded transactions recorded requiring credit note verification.`
      : 'No refunds recorded in this batch.',
  ];

  const riskAnalysis =
    healthScore === 'Optimal'
      ? 'Books are balanced with high ledger concordance. Automated journal posting can proceed for all Exact Matches.'
      : healthScore === 'Attention Required'
      ? 'Action needed on unlinked Razorpay receipts and partial bill entries to prevent revenue recognition discrepancies.'
      : 'Immediate audit intervention recommended: High variance detected in unbilled payments and over-billed vouchers.';

  return {
    headline: `Batch Analysis: ${summary.totalGroups} Payment Groups Reconciled in ${summary.processingTimeMs}ms`,
    healthScore,
    bulletPoints,
    riskAnalysis,
  };
}
